// Deterministic gallery stress tour — the testing protocol for the mobile
// world view (and a baseline for every other gallery variant).
//
// Runs a seeded ~90s sequence of pans, flings, pinches, deep zooms and
// collection jumps against the live gallery instance while sampling frame
// times, long tasks, heap (Chrome only) and JS errors, then renders a
// pass/fail verdict and exposes a JSON report on window.__stressReport.
//
// Three consumers:
//   1. Real iPhone: open …/collections?worldview=1&stress=1 and tap "Run stress
//      tour" — one tap, no debugger needed. iOS can't be driven by Playwright;
//      this IS the on-device automation.
//   2. Playwright (tests/worldview.spec.js): …&stress=1&stressauto=1 starts the
//      tour immediately; the spec awaits window.__stressReport and asserts.
//   3. Manual desktop profiling alongside ?perf=1.
//
// Crash detection: a sessionStorage marker is set at each phase start and
// cleared on completion. If the page (re)loads with a stale marker, the
// previous run died mid-phase — iOS tab kills/reloads become a recorded FAIL
// instead of silence.

const MARKER = 'jk_stress_phase';

export const THRESHOLDS = {
    avgFps:     45,
    p5Fps:      24,   // 5th percentile
    maxOver200: 1,    // frames longer than 200ms
    maxOver500: 0,
    maxErrors:  0,
};

function mulberry32(seed) {
    let a = seed >>> 0;
    return () => {
        a |= 0; a = (a + 0x6D2B79F5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

const sleep = ms => new Promise(r => setTimeout(r, ms));

function waitSettle(g, timeout = 4000) {
    const t0 = performance.now();
    return new Promise(resolve => {
        const check = () => {
            const still = Math.abs(g.velX) < 0.03 && Math.abs(g.velY) < 0.03
                       && Math.abs(g.zoomVel) < 0.001 && !g._dragging;
            if (still || performance.now() - t0 > timeout) resolve();
            else requestAnimationFrame(check);
        };
        check();
    });
}

function fling(g, vx, vy) {
    g.velX = vx;
    g.velY = vy;
    g._wake();
}

// Zoom bounds: instance fields on collections/wall; videos only has module
// constants, so fall back to safe generic bounds.
const zMin = g => g.ZOOM_MIN ?? 0.1;
const zMax = g => g.ZOOM_MAX ?? 2;

// Animated zoom built on zoomAt() — the one primitive every gallery variant
// (collections canvas/dom/worldview, wall, videos) implements with the same
// signature. Deliberately NOT gallery.animateZoom: wall/videos take no
// onComplete callback, so awaiting them would hang.
function zoomTo(g, targetZ, cx, cy) {
    cx = cx ?? g.W / 2;
    cy = cy ?? g.H / 2;
    const target = Math.max(zMin(g), Math.min(zMax(g), targetZ));
    return new Promise(resolve => {
        const startZ = g.zoom;
        const t0 = performance.now();
        const tick = now => {
            const t = Math.min(1, (now - t0) / 300);
            const e = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
            const z = startZ + (target - startZ) * e;
            g.zoomAt(cx, cy, z / g.zoom);
            g._wake();
            if (t < 1) requestAnimationFrame(tick);
            else resolve();
        };
        requestAnimationFrame(tick);
    });
}

// ── Phases ────────────────────────────────────────────────────────────────────
// Each drives the gallery's public surface + velocity fields so the tour works
// on the world view, the stable single-collection view and the desktop canvas.

const PHASES = [
    { name: 'settle-init', run: async () => { await sleep(2000); } },

    { name: 'slow-pan', run: async (g) => {
        // Glide down through the world, then back up.
        for (const dir of [-1, 1]) {
            const t0 = performance.now();
            const drive = () => {
                if (performance.now() - t0 > 4000) return;
                g.panY += dir * 6;
                g._syncWorld(); g._wake();
                requestAnimationFrame(drive);
            };
            drive();
            await sleep(4200);
        }
    } },

    { name: 'flings', run: async (g, rand) => {
        for (let i = 0; i < 4; i++) {
            fling(g, 0, (i % 2 ? 3.5 : -3.5) * (0.8 + rand() * 0.4));
            await waitSettle(g);
        }
        for (let i = 0; i < 4; i++) {
            fling(g, (i % 2 ? 3.5 : -3.5) * (0.8 + rand() * 0.4), 0);
            await waitSettle(g);
        }
    } },

    { name: 'gate-crossing', run: async (g, rand) => {
        // Zoom across the world view's mount gate (0.38–0.45) repeatedly, with
        // horizontal flings in between — the mount/unmount churn hotspot.
        // On other variants this is still a useful mid-zoom oscillation.
        for (let i = 0; i < 3; i++) {
            await zoomTo(g, 0.55, g.W * rand(), g.H * rand());
            fling(g, i % 2 ? 3 : -3, 0);
            await waitSettle(g);
            await zoomTo(g, Math.max(zMin(g), 0.28));
            await sleep(300);
        }
    } },

    { name: 'deep-zoom', run: async (g, rand) => {
        // Max zoom + item-to-item pans (tier-2 / hi-res traffic). Collections
        // variants have navigateTo; wall/videos don't — for those, zoom to max
        // around seeded points and fling between them instead.
        const items = g.items || [];
        if (!items.length) return;
        if (typeof g.navigateTo === 'function') {
            const start = Math.floor(rand() * items.length);
            g.navigateTo(items[start].id, false, true);
            await sleep(800);
            await zoomTo(g, zMax(g));
            for (let i = 1; i <= 6; i++) {
                const it = items[(start + i) % items.length];
                g.navigateTo(it.id, false, true);
                await sleep(700);
            }
        } else {
            await zoomTo(g, zMax(g) * 0.8, g.W * rand(), g.H * rand());
            for (let i = 0; i < 6; i++) {
                fling(g, (i % 2 ? 2.5 : -2.5), i % 3 ? 0 : 1.5);
                await waitSettle(g);
            }
        }
    } },

    { name: 'zoom-out-mass-unmount', run: async (g) => {
        await zoomTo(g, zMin(g));
        await sleep(1500); // give the shed loop time — this is criterion 4
    } },

    { name: 'collection-jumps', run: async (g, rand) => {
        // Collections: _collectionList ({key,label}); wall: _categoryList
        // (plain strings); videos: none (skip).
        const list = g._collectionList || g._categoryList;
        const keys = (list?.map(c => (typeof c === 'string' ? c : c.key)) ?? g.groups?.map(gr => gr.key) ?? [])
            .filter(k => k && k !== 'all');
        if (!keys.length || typeof g.setFilter !== 'function') return;
        for (let i = 0; i < 8; i++) {
            g.setFilter(keys[Math.floor(rand() * keys.length)]);
            await sleep(900);
        }
    } },

    { name: 'pinch-thrash', run: async (g, rand) => {
        // Rapid zoom oscillation around random midpoints — tier thrash.
        for (let cycle = 0; cycle < 6; cycle++) {
            const cx = g.W * (0.2 + rand() * 0.6);
            const cy = g.H * (0.2 + rand() * 0.6);
            const dir = cycle % 2 ? 0.96 : 1.045;
            for (let f = 0; f < 20; f++) {
                g.zoomAt(cx, cy, dir);
                g._wake();
                await sleep(16);
            }
        }
        await waitSettle(g);
    } },

    { name: 'settle-end', run: async () => { await sleep(3000); } },
];

// ── Sampler ───────────────────────────────────────────────────────────────────

function createSampler(gallery) {
    const s = {
        deltas: [], phaseOf: [], currentPhase: '',
        longTasks: 0, errors: 0, heapSamples: [], mountedPeak: 0,
        _stop: false,
    };
    let last = performance.now();
    const frame = now => {
        if (s._stop) return;
        s.deltas.push(now - last);
        s.phaseOf.push(s.currentPhase);
        last = now;
        // _mounted Set on collections/wall; videos tracks mount state on item._el.
        const mounted = gallery._mounted?.size
            ?? gallery._stats?.sprites
            ?? (gallery.items ? gallery.items.reduce((n, i) => n + (i._el ? 1 : 0), 0) : 0);
        if (mounted > s.mountedPeak) s.mountedPeak = mounted;
        requestAnimationFrame(frame);
    };
    requestAnimationFrame(t => { last = t; requestAnimationFrame(frame); });

    s.onError = () => { s.errors++; };
    window.addEventListener('error', s.onError);
    window.addEventListener('unhandledrejection', s.onError);

    try {
        s.lt = new PerformanceObserver(list => { s.longTasks += list.getEntries().length; });
        s.lt.observe({ type: 'longtask' });
    } catch { /* not supported (Safari) */ }

    s.heapTimer = setInterval(() => {
        const m = performance.memory;
        if (m) s.heapSamples.push(Math.round(m.usedJSHeapSize / 1048576));
    }, 1000);

    s.stop = () => {
        s._stop = true;
        window.removeEventListener('error', s.onError);
        window.removeEventListener('unhandledrejection', s.onError);
        s.lt?.disconnect();
        clearInterval(s.heapTimer);
    };
    return s;
}

function summarise(sampler, prevRunDied, variant) {
    // Ignore the first few frames (page still settling into the rAF cadence).
    const deltas = sampler.deltas.slice(5);
    const phases = sampler.phaseOf.slice(5);
    const fpsOf  = arr => arr.length ? 1000 / (arr.reduce((a, b) => a + b, 0) / arr.length) : 0;

    const sorted = [...deltas].sort((a, b) => a - b);
    const p95d   = sorted[Math.floor(sorted.length * 0.95)] || 0;

    const perPhase = {};
    deltas.forEach((d, i) => {
        const p = phases[i] || '?';
        (perPhase[p] = perPhase[p] || []).push(d);
    });

    const report = {
        variant,
        ua: navigator.userAgent,
        when: new Date().toISOString(),
        prevRunDied,
        thresholds: THRESHOLDS,
        fps: {
            avg: +fpsOf(deltas).toFixed(1),
            p5:  +(1000 / p95d).toFixed(1),  // 95th-percentile delta ⇒ 5th-percentile fps
        },
        frames: {
            total:   deltas.length,
            over50:  deltas.filter(d => d > 50).length,
            over100: deltas.filter(d => d > 100).length,
            over200: deltas.filter(d => d > 200).length,
            over500: deltas.filter(d => d > 500).length,
            worstMs: +Math.max(0, ...deltas).toFixed(0),
        },
        longTasks:   sampler.longTasks,
        errors:      sampler.errors,
        mountedPeak: sampler.mountedPeak,
        heap: sampler.heapSamples.length
            ? { startMB: sampler.heapSamples[0], peakMB: Math.max(...sampler.heapSamples), endMB: sampler.heapSamples.at(-1) }
            : null,
        phases: Object.entries(perPhase).map(([name, ds]) => ({
            name,
            fps: +fpsOf(ds).toFixed(1),
            worstMs: +Math.max(0, ...ds).toFixed(0),
        })),
    };

    report.failures = [];
    if (prevRunDied)                                report.failures.push(`previous run died during "${prevRunDied}"`);
    if (report.fps.avg < THRESHOLDS.avgFps)         report.failures.push(`avg fps ${report.fps.avg} < ${THRESHOLDS.avgFps}`);
    if (report.fps.p5 < THRESHOLDS.p5Fps)           report.failures.push(`p5 fps ${report.fps.p5} < ${THRESHOLDS.p5Fps}`);
    if (report.frames.over200 > THRESHOLDS.maxOver200) report.failures.push(`${report.frames.over200} frames > 200ms`);
    if (report.frames.over500 > THRESHOLDS.maxOver500) report.failures.push(`${report.frames.over500} frames > 500ms`);
    if (report.errors > THRESHOLDS.maxErrors)       report.failures.push(`${report.errors} JS errors`);
    report.pass = report.failures.length === 0;
    return report;
}

// ── UI ────────────────────────────────────────────────────────────────────────

function makePanel() {
    const el = document.createElement('div');
    el.style.cssText = `
        position:fixed; top:10px; left:10px; right:10px; z-index:99999;
        background:rgba(0,0,0,.88); color:#fff; font:12px/1.5 monospace;
        padding:10px 12px; border-radius:6px; pointer-events:auto; max-height:70vh; overflow:auto;
    `;
    document.body.appendChild(el);
    return el;
}

function renderReport(panel, report) {
    const rows = report.phases.map(p =>
        `<tr><td style="padding-right:12px">${p.name}</td><td style="padding-right:12px">${p.fps}fps</td><td>${p.worstMs}ms worst</td></tr>`).join('');
    panel.innerHTML = `
        <div style="font-size:15px;margin-bottom:6px;color:${report.pass ? '#4ade80' : '#f87171'}">
            ${report.pass ? 'PASS' : 'FAIL'} — avg ${report.fps.avg}fps, p5 ${report.fps.p5}fps,
            worst ${report.frames.worstMs}ms, ${report.errors} errors${report.heap ? `, heap peak ${report.heap.peakMB}MB` : ''}
        </div>
        ${report.failures.map(f => `<div style="color:#f87171">✗ ${f}</div>`).join('')}
        <table style="margin-top:6px">${rows}</table>
        <button id="stress-copy" style="margin-top:8px;padding:4px 10px;background:#333;color:#fff;border:1px solid #666;border-radius:4px">Copy JSON</button>
    `;
    panel.querySelector('#stress-copy').addEventListener('click', () => {
        navigator.clipboard?.writeText(JSON.stringify(report, null, 2));
    });
}

// ── Entry ─────────────────────────────────────────────────────────────────────

export async function runStressTour(gallery, { auto = false, variant: variantIn } = {}) {
    const prevRunDied = sessionStorage.getItem(MARKER);
    sessionStorage.removeItem(MARKER);

    const panel = makePanel();
    const variant = variantIn || window.JK?.galleryVariant || gallery.constructor?.name || 'unknown';

    if (prevRunDied) {
        panel.innerHTML = `<div style="color:#f87171;font-size:14px">⚠ previous stress run died during "${prevRunDied}" (tab killed/reloaded)</div>`;
    }

    if (!auto) {
        await new Promise(resolve => {
            const btn = document.createElement('button');
            btn.textContent = `Run stress tour (${variant})`;
            btn.style.cssText = 'margin-top:6px;padding:8px 14px;background:#222;color:#fff;border:1px solid #666;border-radius:4px;font:13px monospace';
            panel.appendChild(btn);
            btn.addEventListener('click', () => { btn.remove(); resolve(); });
        });
    }

    const sampler = createSampler(gallery);
    for (const phase of PHASES) {
        sampler.currentPhase = phase.name;
        sessionStorage.setItem(MARKER, phase.name);
        panel.innerHTML = `<div>stress tour: <b>${phase.name}</b> (${PHASES.indexOf(phase) + 1}/${PHASES.length})${prevRunDied ? ` — ⚠ prev run died in "${prevRunDied}"` : ''}</div>`;
        const rand = mulberry32(42 + PHASES.indexOf(phase)); // per-phase seed: deterministic even if a phase early-returns
        try {
            await phase.run(gallery, rand);
        } catch (err) {
            console.error(`stress phase ${phase.name} failed`, err);
            sampler.errors++;
        }
        await waitSettle(gallery);
    }
    sessionStorage.removeItem(MARKER);
    sampler.stop();

    const report = summarise(sampler, prevRunDied, variant);
    window.__stressReport = report;
    renderReport(panel, report);
    return report;
}

// Called from app.js when ?stress=1. Waits for whichever gallery this page
// hosts (collections/wall/videos) to exist and have data, then arms the tour
// (button) or starts it (?stressauto=1).
export function initStressTour() {
    const params = new URLSearchParams(location.search);
    if (!params.has('stress')) return;
    const t0 = performance.now();
    const poll = () => {
        const jk = window.JK || {};
        const [g, variant] =
            jk.gallery?.items?.length     ? [jk.gallery, jk.galleryVariant] :
            jk.wallGallery?.items?.length ? [jk.wallGallery, 'wall'] :
            jk.videoWall?.items?.length   ? [jk.videoWall, 'videos'] :
            [null, null];
        if (g) {
            runStressTour(g, { auto: params.has('stressauto'), variant });
        } else if (performance.now() - t0 < 20000) {
            setTimeout(poll, 200);
        } else {
            const panel = makePanel();
            panel.innerHTML = '<div style="color:#f87171">stress tour: no gallery on this page (20s timeout)</div>';
        }
    };
    poll();
}
